import { useState, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, ApiException, getAccessToken } from '../../lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Spinner } from '../../components/ui/spinner';
import { Modal } from '../../components/ui/modal';
import { fmtDateTime } from '../../lib/format';
import { Paperclip, Upload, Download, Trash2, FileText, AlertCircle } from 'lucide-react';

interface Anexo {
  id: string;
  contratoId: string;
  tipo: string;
  nomeOriginal: string;
  mimeType: string;
  tamanhoBytes: number;
  checksumSha256: string;
  createdAt: string;
  uploader: { id: string; nome: string } | null;
}

const tipoLabel: Record<string, string> = {
  contrato_assinado: 'Contrato assinado',
  autorizacao_desconto: 'Autorização de desconto',
  comprovante: 'Comprovante',
  averbacao: 'Averbação',
  outro: 'Outro',
};

const tipoColor: Record<string, string> = {
  contrato_assinado: 'bg-blue-100 text-blue-900',
  autorizacao_desconto: 'bg-green-100 text-green-900',
  comprovante: 'bg-purple-100 text-purple-900',
  averbacao: 'bg-amber-100 text-amber-900',
  outro: 'bg-gray-100 text-gray-700',
};

function fmtSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
}

export function AnexosCard({ contratoId }: { contratoId: string }) {
  const qc = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploadModal, setUploadModal] = useState(false);
  const [tipo, setTipo] = useState<keyof typeof tipoLabel>('contrato_assinado');
  const [arquivoSelecionado, setArquivoSelecionado] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<Anexo | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['anexos', contratoId],
    queryFn: () => api<{ items: Anexo[]; total: number }>(`/anexos/contrato/${contratoId}`),
  });

  const upload = useMutation({
    mutationFn: async () => {
      if (!arquivoSelecionado) throw new Error('Selecione um arquivo');
      const fd = new FormData();
      fd.append('arquivo', arquivoSelecionado);
      return api<Anexo>(`/anexos/contrato/${contratoId}/upload?tipo=${tipo}`, {
        method: 'POST', formData: fd,
      });
    },
    onSuccess: () => {
      setUploadModal(false);
      setArquivoSelecionado(null);
      setError(null);
      qc.invalidateQueries({ queryKey: ['anexos', contratoId] });
    },
    onError: (err: ApiException) => setError(err.apiError?.message ?? 'Falha no upload'),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api(`/anexos/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      setConfirmDelete(null);
      qc.invalidateQueries({ queryKey: ['anexos', contratoId] });
    },
  });

  // Download usa fetch direto pra anexar header de auth
  async function baixar(anexoId: string, nome: string) {
    const token = getAccessToken();
    const r = await fetch(`/api/v1/anexos/${anexoId}/download`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!r.ok) return alert('Falha ao baixar arquivo');
    const blob = await r.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = nome;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex justify-between items-start">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <Paperclip className="h-4 w-4" />Anexos
            </CardTitle>
            <CardDescription>{data?.total ?? 0} arquivo(s)</CardDescription>
          </div>
          <Button size="sm" onClick={() => { setUploadModal(true); setError(null); setArquivoSelecionado(null); }}>
            <Upload className="h-3 w-3 mr-1" />Adicionar
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading && <Spinner />}
        {!isLoading && data && data.items.length === 0 && (
          <p className="text-sm text-muted-foreground text-center py-4">
            Nenhum anexo. Adicione contrato assinado, autorização de desconto, comprovantes, etc.
          </p>
        )}
        {!isLoading && data && data.items.length > 0 && (
          <div className="space-y-2">
            {data.items.map((a) => (
              <div key={a.id} className="flex items-center gap-3 p-2 hover:bg-muted/30 rounded border">
                <FileText className="h-4 w-4 text-muted-foreground shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-sm truncate" title={a.nomeOriginal}>{a.nomeOriginal}</span>
                    <span className={`text-xs px-2 py-0.5 rounded ${tipoColor[a.tipo] ?? 'bg-gray-100'}`}>
                      {tipoLabel[a.tipo] ?? a.tipo}
                    </span>
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {fmtSize(a.tamanhoBytes)} · {a.uploader?.nome ?? 'desconhecido'} · {fmtDateTime(a.createdAt)}
                  </div>
                </div>
                <Button size="sm" variant="ghost" onClick={() => baixar(a.id, a.nomeOriginal)}>
                  <Download className="h-3 w-3" />
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setConfirmDelete(a)}>
                  <Trash2 className="h-3 w-3 text-destructive" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </CardContent>

      <Modal
        open={uploadModal}
        onClose={() => { setUploadModal(false); setError(null); }}
        title="Adicionar anexo"
        description="PDF, JPG, PNG, WEBP ou HEIC · até 25 MB"
      >
        <div className="space-y-4">
          <div className="space-y-1">
            <label className="text-sm font-medium" htmlFor="tipo-anexo">Tipo</label>
            <select id="tipo-anexo" value={tipo}
              onChange={(e) => setTipo(e.target.value as any)}
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm">
              {Object.entries(tipoLabel).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </div>

          <div
            onClick={() => fileRef.current?.click()}
            className="border-2 border-dashed rounded-lg p-6 text-center cursor-pointer hover:border-primary/50 transition-colors"
          >
            <Upload className="h-6 w-6 mx-auto text-muted-foreground mb-2" />
            {arquivoSelecionado ? (
              <div>
                <p className="text-sm font-medium">{arquivoSelecionado.name}</p>
                <p className="text-xs text-muted-foreground">{fmtSize(arquivoSelecionado.size)}</p>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">Clique para selecionar um arquivo</p>
            )}
            <input
              ref={fileRef}
              type="file"
              accept="application/pdf,image/jpeg,image/png,image/webp,image/heic,image/heif"
              className="hidden"
              onChange={(e) => setArquivoSelecionado(e.target.files?.[0] ?? null)}
            />
          </div>

          {error && (
            <div className="flex items-start gap-2 p-3 border border-destructive/30 bg-destructive/10 rounded text-sm text-destructive">
              <AlertCircle className="h-4 w-4 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          <div className="flex gap-2 justify-end">
            <Button variant="ghost" onClick={() => setUploadModal(false)} disabled={upload.isPending}>Cancelar</Button>
            <Button onClick={() => upload.mutate()} disabled={!arquivoSelecionado || upload.isPending}>
              {upload.isPending ? <><Spinner className="mr-2 h-4 w-4" />enviando…</> : 'Enviar'}
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        open={confirmDelete !== null}
        onClose={() => setConfirmDelete(null)}
        title="Excluir anexo?"
        description="Esta ação não pode ser desfeita"
      >
        {confirmDelete && (
          <div className="space-y-4">
            <p className="text-sm">
              Excluir <strong>{confirmDelete.nomeOriginal}</strong> ({tipoLabel[confirmDelete.tipo]})?
            </p>
            <div className="flex gap-2 justify-end">
              <Button variant="ghost" onClick={() => setConfirmDelete(null)} disabled={remove.isPending}>Cancelar</Button>
              <Button variant="danger" onClick={() => remove.mutate(confirmDelete.id)} disabled={remove.isPending}>
                {remove.isPending ? <><Spinner className="mr-2 h-4 w-4" />excluindo…</> : 'Excluir'}
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </Card>
  );
}
